import styles from "./client.module.scss";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppContext, AppAction } from "helpers/context";
import {
	Box, Button, IconButton, Table, TableBody, TableCell,
	TableHead, TableRow, Toolbar, TableSortLabel, Tooltip, TablePagination
} from "@mui/material";

import SearchInput from "./search-input/search-input";
import StartTimeDropdown from "./start-time-dropdown/start-time-dropdown";
import EndTimeDropdown from "./end-time-dropdown/end-time-dropdown";
import AddModal from "./add-modal/add-modal";
import DeleteModal from "./delete-modal/delete-modal";
import { getApiClient } from "api/layer";

const Client = () => {
	const [context, dispatch] = useContext(AppContext);
	const [t] = useTranslation();
	const [showFilters, setShowFilters] = useState(false);
	const [open, setOpen] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [currentInfo, setCurrentInfo] = useState<any>();
	const [searchData, setSearchData] = useState<{
		keyWord: string;
		startTime: any;
		endTime: any;
	}>({
		keyWord: "",
		startTime: null,
		endTime: null,
	});
	const [reverseSelect, setReverseSelect] = useState(false);
	const [page, setPage] = useState(0);
	const [tableData, setTableData] = useState([]);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [hoveredRow, setHoveredRow] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [sortByField, setSortByField] = useState<"client_code" | "client_name" | "key_count" | "created_time">("client_code");
	const [sortOrder, setSortOrder] = useState<{
		client_code: "asc" | "desc",
		client_name: "asc" | "desc",
		key_count: "asc" | "desc",
		created_time: "asc" | "desc",
	}>({
		client_code: "asc",
		client_name: "asc",
		key_count: "asc",
		created_time: "asc",
	});

	const toggleFilters = () => {
		setShowFilters((prev) => !prev);
	};

	const clearFilters = () => {
		setShowFilters(false);
	};

	const handleSearch = (params: any, is: boolean) => {
		setSearchData((prevData) => ({ ...prevData, [params.name]: params.value }));
		if ((params.name === "startTime" || params.name === "endTime") && params.value && params.value.format("YYYY-MM-DD") === "Invalid Date") {
			return;
		}
		setReverseSelect(is);
		setPage(0);
	};

	const handleDelete = (item: any) => {
		setCurrentInfo(item);
		setOpenDeleteModal(true);
	};

	const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleSave = () => {
		setOpen(false);
		initData();
	};

	const initData = async () => {
		setIsLoading(true);
		let pageParam = {
			pageNo: page + 1,
			pageSize: rowsPerPage,
			reverseSelect: reverseSelect,
			sortOrderAsc: sortOrder[sortByField] === "asc",
			sortByField: sortByField,
			...searchData
		};

		let params: any = { ...searchData };

		if (searchData.startTime) {
			params.startTime = searchData.startTime.format("YYYY-MM-DD");
		}

		if (searchData.endTime) {
			params.endTime = searchData.endTime.format("YYYY-MM-DD");
		}

		getApiClient(params, pageParam).then((res: any) => {
			if (res.code !== 200 || !res.data?.rows) {
				return;
			}
			setTableData(res.data.rows);
			setTotalCount(res.data.totalCount * 1 || 0);
			setIsLoading(false);
		});
	};


	useEffect(() => {
		initData();
	}, [page, rowsPerPage, searchData, reverseSelect, sortByField, sortOrder]);

	return (<div className={styles.container}>
		<div className={styles.title}>{t("userSystem.client")}</div>
		<Toolbar style={{ justifyContent: "space-between", borderBottom: "1px solid #ccc", marginBottom: "5px", padding: "0" }}>
			<Button
				variant="contained"
				color="success"
				size="small"
				endIcon={<span className="material-icons">add</span>}
				onClick={() => {
					setOpen(true);
					setCurrentInfo(null);
				}}
			>
				{t("userSystem.addClient")}
			</Button>

			<IconButton
				color="success"
				onClick={toggleFilters}
				style={{
					color: showFilters ? "#2e7d32" : "rgba(0, 0, 0, 0.54)",
					background: showFilters ? "#c1e5ca" : "",
				}}
			>
				<span className="material-icons">filter_list</span>
			</IconButton>
		</Toolbar>

		{showFilters && (
			<Box style={{ display: "flex", alignItems: "center", padding: "8px", backgroundColor: "#f5f5f5", justifyContent: "space-between" }}>
				<Box style={{ display: "flex", flex: 1, alignItems: "center" }}>
					<SearchInput onSearch={handleSearch} />

					<StartTimeDropdown
						defaultTime={searchData.startTime}
						onSave={(value) => handleSearch({ name: "startTime", value }, reverseSelect)}
					/>
					<EndTimeDropdown
						defaultTime={searchData.endTime}
						onSave={(value) => handleSearch({ name: "endTime", value }, reverseSelect)}
					/>
				</Box>
				<IconButton
					onClick={clearFilters}
					aria-label="clear"
					color="default"
				>
					<span className="material-icons">clear</span>
				</IconButton>
			</Box>
		)}

		<div style={{ flex: 1, overflow: "auto" }}>
			<Table
				stickyHeader
				aria-label="sticky table"
				size="small"
				style={{
					tableLayout: "fixed",
					width: "100%",
					minWidth: "100%"
				}}
			>
				<TableHead>
					<TableRow>
						<TableCell>
							<TableSortLabel
								active={sortByField === "client_code"}
								onClick={() => {
									setSortByField("client_code")
									let sort: any = {
										...sortOrder,
										client_code: sortOrder.client_code === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.client_code}
							>
								{t("userSystem.clientCode")}
							</TableSortLabel>
						</TableCell>
						<TableCell>
							<TableSortLabel
								active={sortByField === "client_name"}
								onClick={() => {
									setSortByField("client_name")
									let sort: any = {
										...sortOrder,
										client_name: sortOrder.client_name === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.client_name}
							>
								{t("userSystem.clientName")}
							</TableSortLabel>
						</TableCell>
						<TableCell>
							<TableSortLabel
								active={sortByField === "key_count"}
								onClick={() => {
									setSortByField("key_count")
									let sort: any = {
										...sortOrder,
										key_count: sortOrder.key_count === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.key_count}
							>
								{t("userSystem.keyCount")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{
							width: 180,
							whiteSpace: "nowrap",
						}}>
							<TableSortLabel
								active={sortByField === "created_time"}
								onClick={() => {
									setSortByField("created_time")
									let sort: any = {
										...sortOrder,
										created_time: sortOrder.created_time === "asc" ? "desc" : "asc"
									}
									setSortOrder(sort)
								}}
								direction={sortOrder.created_time}
							>
								{t("userSystem.createdTime")}
							</TableSortLabel>
						</TableCell>
						<TableCell style={{ padding: 0, width: 0 }}></TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{isLoading ? (
						<TableRow>
							<TableCell colSpan={4}>
								<div className={styles.loading}>
									<div className="loading loading-spinner loading-lg"></div>
									<div>{t("layers.loading")}</div>
								</div>
							</TableCell>
						</TableRow>
					) : (
						tableData.map((item: any) => (
							<TableRow
								key={item.id}
								hover
								onMouseEnter={() => setHoveredRow(item.id)}
								onMouseLeave={() => setHoveredRow(null)}
								className={styles.row}
							>
								<TableCell>{item.clientCode}</TableCell>
								<TableCell style={{ wordBreak: "break-all" }}>{item.clientName}</TableCell>
								<TableCell>{item.keyCount}</TableCell>
								<TableCell>{item.createdTime}</TableCell>
								<TableCell style={{ padding: 0, position: "relative" }}>
									{hoveredRow === item.id && (
										<div
											className={`${styles.icons}`}
										>
											<Tooltip
												title={t("tooltip.edit")}
											>
												<IconButton
													onClick={() => {
														setOpen(true);
														setCurrentInfo(item);
													}}
												>
													<span className="material-icons">edit</span>
												</IconButton>
											</Tooltip>

											<Tooltip
												title={t("tooltip.delete")}
											>
												<IconButton onClick={() => handleDelete(item)}>
													<span className="material-icons">delete</span>
												</IconButton>
											</Tooltip>
										</div>
									)}
								</TableCell>
							</TableRow>
						))
					)}
				</TableBody>
			</Table>
		</div>
		<div style={{ marginLeft: "auto" }}>
			<TablePagination
				component="div"
				count={totalCount}
				rowsPerPageOptions={[3, 10, 15, 25]}
				page={page}
				onPageChange={handleChangePage}
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				labelRowsPerPage={t("userSystem.rowsPerPage")}
				slotProps={{
					select: {
						native: true,
					},
				}}
			/>
		</div>

		{open && (
			<AddModal
				open={open}
				onClose={() => setOpen(false)}
				onSave={handleSave}
				data={currentInfo}
			/>
		)}

		{openDeleteModal && (
			<DeleteModal
				data={currentInfo}
				initData={initData}
				onClose={() => setOpenDeleteModal(false)}
			/>
		)}
	</div>)
};

export default Client;
